/**
 * Chart theme adapter.
 *
 * Maps Neeve DLS color tokens (the canonical PIE_COLORS / BAR_COLORS palettes
 * from `@/utils/chart-colors`) into each underlying chart library's theming
 * shape. Switching libraries via the Dev Console slicer should never change
 * the rendered palette — that's the job of this adapter.
 *
 * The contract: every implementation in `recharts/`, `visx/`, `d3/`,
 * `echarts/` reads its colors and styling defaults via `chartTheme(lib)` and
 * never hardcodes hex values.
 */

import { PIE_COLORS, BAR_COLORS } from '@/utils/chart-colors';
import type { ChartLib } from './types';

/**
 * Status color tokens — should match the CSS variables wired in index.css.
 * Used for status-coded charts (e.g., online/offline distributions).
 */
const STATUS_COLORS = {
  online: 'var(--dls-status-online)',
  offline: 'var(--dls-status-offline)',
  warning: 'var(--dls-status-warning)',
  unknown: 'var(--dls-status-unknown)',
} as const;

/**
 * Library-neutral theme bundle. Each library adapter shapes this into its own
 * theme config (e.g., ECharts has a `theme` object, Visx accepts inline props,
 * D3 reads from D3-scale).
 */
export interface ChartTheme {
  /** Categorical palette for pie/donut/bar charts. */
  categoricalPalette: readonly string[];
  /** Status palette for state-coded charts. */
  statusPalette: typeof STATUS_COLORS;
  /** Default text color (resolves against DLS foreground token at render time). */
  textColor: string;
  /** Default axis/grid line color. */
  gridColor: string;
  /** Default font family. */
  fontFamily: string;
  /** Default font size in px. */
  fontSize: number;
}

/**
 * Build the base theme bundle. All four library adapters share this.
 */
function baseTheme(): ChartTheme {
  return {
    categoricalPalette: PIE_COLORS, // PIE_COLORS and BAR_COLORS are intentionally identical
    statusPalette: STATUS_COLORS,
    textColor: 'var(--dls-foreground)',
    gridColor: 'var(--dls-border)',
    fontFamily: 'var(--dls-font-sans, "Source Sans 3", sans-serif)',
    fontSize: 12,
  };
}

/**
 * Library-specific config blob. Each adapter returns the shape its target
 * library expects.
 */
export interface RechartsThemeConfig {
  lib: 'recharts';
  theme: ChartTheme;
  /** Recharts-specific defaults — props to pass to wrapper components. */
  axis: { stroke: string; tick: { fill: string; fontSize: number; fontFamily: string } };
  legend: { fontFamily: string; fontSize: number };
}

export interface VisxThemeConfig {
  lib: 'visx';
  theme: ChartTheme;
}

export interface D3ThemeConfig {
  lib: 'd3';
  theme: ChartTheme;
}

export interface EchartsThemeConfig {
  lib: 'echarts';
  theme: ChartTheme;
  /** ECharts theme object passed to its registerTheme/init. */
  echartsTheme: {
    color: readonly string[];
    textStyle: { color: string; fontFamily: string; fontSize: number };
    grid: { borderColor: string };
  };
}

export type LibThemeConfig =
  | RechartsThemeConfig
  | VisxThemeConfig
  | D3ThemeConfig
  | EchartsThemeConfig;

/**
 * Resolve the theme config for the given library. Pure function — call from
 * any chart implementation to get its styling defaults.
 *
 * Generic so callers receive the lib-specific config shape without manual
 * narrowing. `chartTheme('recharts')` returns RechartsThemeConfig, etc.
 */
export function chartTheme<L extends ChartLib>(lib: L): Extract<LibThemeConfig, { lib: L }>;
export function chartTheme(lib: ChartLib): LibThemeConfig {
  const theme = baseTheme();
  switch (lib) {
    case 'recharts':
      return {
        lib: 'recharts',
        theme,
        axis: {
          stroke: theme.gridColor,
          tick: { fill: theme.textColor, fontSize: theme.fontSize, fontFamily: theme.fontFamily },
        },
        legend: { fontFamily: theme.fontFamily, fontSize: theme.fontSize },
      };
    case 'visx':
      return { lib: 'visx', theme };
    case 'd3':
      return { lib: 'd3', theme };
    case 'echarts':
      return {
        lib: 'echarts',
        theme,
        echartsTheme: {
          color: theme.categoricalPalette,
          textStyle: { color: theme.textColor, fontFamily: theme.fontFamily, fontSize: theme.fontSize },
          grid: { borderColor: theme.gridColor },
        },
      };
  }
}

// Re-export for convenience.
export { PIE_COLORS, BAR_COLORS };
